<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { computed, inject, onMounted, ref, type Ref, watchEffect, watch } from 'vue';
import { appUrl, instance as api } from '@/helpers/api';
import PrevNextBtnBar from '@/components/PrevNextBtnBar.vue';
import { errToStr, markPpaSubmitRouteReached } from '@/helpers/tools';
import { sectionNameToRoute } from '@/helpers/planungsgeschaeft';
import planungsgeschaeftAbschlussErrorImg from '@/assets/images/planungsgeschaeft.abschluss.error.png';
import { globalVar, useAuthUser } from '@/helpers/globals';
import { ECH_STATUS } from '@/models/Status';
import UserLoginModalCircus from '@/components/UserLoginModalCircus.vue';

type CheckResultSectionEntries = {
    [key: string]: {
        valid: boolean;
    } & {
        [key: string]: {
            valid: boolean;
            message: string;
        };
    };
};

type CheckResult = {
    valid: boolean;
    sections: CheckResultSectionEntries;
};

// injected from PlanungsgeschaeftEditView
const saving = inject('saving') as Ref<boolean>;
const readonly = inject('readonly') as Ref<boolean>;
const showLoginCircus = ref(false);

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const emit = defineEmits<{
    'geschaeft-changed': [section: string | null];
    next: [];
    prev: [];
}>();

const confirmComplete = defineModel('confirmComplete', { default: false });
const confirmAgb = defineModel('confirmAgb', { default: false });

const checkInProgress: Ref<boolean> = ref(false);
const checkResult: Ref<CheckResult | null> = ref(null);
const showEinreichung: Ref<boolean> = ref(false);
const error: Ref<string | null> = ref(null);
const qrSrc: Ref<string | null> = ref(null);
const signUrl: Ref<string | null> = ref(null);

const ready4Submit = computed(() => {
    // submit only allowed if
    // user agreed to the terms
    // and has a valid configuration for signatures
    return checkResult.value?.valid && confirmComplete.value && confirmAgb.value;
});

watchEffect(() => {
    saving.value = checkInProgress.value;
});

watch(readonly, (oldVal, newVal) => {
    if (newVal && !checkInProgress.value) {
        checkGeschaeft();
    }
});

onMounted(async () => {
    checkGeschaeft();
    checkForSignature();
    // We have reached the Abschluss route: Mark this fact for future validation logic change:
    markPpaSubmitRouteReached(props.geschaeft);
});

function onPrevBtn() {
    emit('prev');
}

async function onSubmitBtn() {
    // if (!ready4Submit.value) {
    //     return;
    // }
    if (useAuthUser()?.has_username_passkey || useAuthUser()?.api_user || globalVar('disable_pg_signatures')) {
        commitPlanungsgeschaeft();
    } else {
        showLoginCircus.value = true;
    }
}

async function checkGeschaeft() {
    // No check if Geschaeft is read only:
    if (readonly.value) {
        checkResult.value = null;
        return;
    }
    checkInProgress.value = true;
    try {
        const res = await api().post('/Planungsgeschaeft/check', {
            ppa_id: props.geschaeft.id
        });
        //----------------------------------
        // TODO: REMOVE: DEV ONLY!
        // res.data.valid = true;
        //----------------------------------
        checkResult.value = res.data;
        showEinreichung.value = res.data?.valid;
    } catch (e) {
        error.value = errToStr(e);
    } finally {
        checkInProgress.value = false;
    }
}

async function commitPlanungsgeschaeft() {
    const user = useAuthUser();
    try {
        saving.value = true;
        const res = await api().post(
            '/Planungsgeschaeft/commit',
            {
                planungsgeschaeft_id: props.geschaeft.id
            },
            {
                timeout: 300000
            }
        );
        //----------------------------------
        // TODO: REMOVE: DEV ONLY!
        // res.data.valid = true;
        //----------------------------------
        checkResult.value = res.data;
        if (checkResult.value?.valid) {
            props.geschaeft.set('aktueller_status_ebau_value', ECH_STATUS.WAITING_FOR_SIGNATURE);
            await checkForSignature();
            if (globalVar('disable_pg_signatures') || !user?.has_username_passkey) {
                document.location.href = appUrl('/geschaefte/meine_geschaefte/' + props.geschaeft.id);
            } else if (signUrl.value) {
                document.location.href = signUrl.value;
            }
        }
    } catch (e) {
        error.value = errToStr(e);
    } finally {
        saving.value = false;
    }
}

async function checkForSignature() {
    if (props.geschaeft.aktueller_status_ebau_value !== ECH_STATUS.WAITING_FOR_SIGNATURE) {
        return;
    } else {
        try {
            const res = await api().post('/planningPermissionApplication/getSignatureUrl', {
                id: props.geschaeft.id
            });
            if (res && res.data.qr) {
                qrSrc.value = res.data.qr;
                signUrl.value = res.data.url;
            }
        } catch (e) {
            console.error(e);
        }
    }
    // -------------------------------
    // TODO: REMOVE: DEV ONLY!
    // qrSrc.value = 'https://www.google.com';
    // signUrl.value = 'https://www.google.com';
    // -------------------------------
}

function createRouteFromSection(section: string) {
    const route = sectionNameToRoute(section);
    if (route) {
        return {
            ...route,
            params: { id: props.geschaeft.id }
        };
    }
    return null;
}
</script>

<template>
    <div>
        <h4 class="ktg-title-04 ktg-inner-box-padding mb-1_5rem">{{ $t('abschluss') }}</h4>
        <fieldset class="ktg-fieldset ktg-form-area-maxwidth py-3_5rem px-6rem">
            <ktg-clues v-if="error" class="my-2rem" appearance="danger">
                <ktg-clue>
                    <ktg-clue-title icon="warning">{{ $t('error') }}</ktg-clue-title>
                    <ktg-clue-text>{{ error }}</ktg-clue-text>
                </ktg-clue>
            </ktg-clues>

            <ktg-clues v-if="checkInProgress" class="my-2rem">
                <ktg-clue>
                    <ktg-clue-title
                        ><div class="flex gap-2">
                            <ktg-icon loading></ktg-icon> {{ $t('check_in_progress') }}
                        </div></ktg-clue-title
                    >
                </ktg-clue>
            </ktg-clues>

            <div v-if="readonly" v-html="$t('ppa_is_not_editable')"></div>
            <div v-if="!readonly">
                <!-- ------------------------ Geschaeft is VALID ------------------------ -->
                <div v-if="!checkInProgress && checkResult?.valid">
                    <h5 class="ktg-title-05 ktg-inner-box-padding mb-1_5rem">{{ $t('kontrolle_freigabe') }}</h5>
                    <ktg-clues class="mb-2_5rem">
                        <ktg-clue>
                            <ktg-clue-title icon="info">
                                <div v-html="$t('no_edit_after_commit_msg_long')" />
                            </ktg-clue-title>
                        </ktg-clue>
                    </ktg-clues>

                    <div>
                        <div class="ktg-inner-box-padding mb-1rem">
                            <ktg-labelled-choice>
                                <ktg-checkbox
                                    :checked="confirmComplete"
                                    input-id="cb-complete"
                                    :readonly="saving"
                                    @change="confirmComplete = $event.target.checked"
                                ></ktg-checkbox>
                                <ktg-label for="cb-complete"><div v-html="$t('fee_warning_new')" /></ktg-label>
                            </ktg-labelled-choice>
                        </div>

                        <div class="ktg-inner-box-padding mt-1rem">
                            <ktg-labelled-choice>
                                <ktg-checkbox
                                    :checked="confirmAgb"
                                    input-id="cb-agb"
                                    :readonly="saving"
                                    @change="confirmAgb = $event.target.checked"
                                ></ktg-checkbox>
                                <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
                                <ktg-label for="cb-agb" v-html="$t('accept_agb_w_link')"></ktg-label>
                            </ktg-labelled-choice>
                        </div>
                    </div>
                </div>

                <!-- ------------------------ Geschaeft is not valid ------------------------ -->
                <div v-if="!checkInProgress && !checkResult?.valid">
                    <h5 class="ktg-title-05 ktg-inner-box-padding mb-1_5rem">
                        {{ $t('geschaeft_nicht_vollstaendig') }}
                    </h5>
                    <ktg-clues appearance="danger">
                        <ktg-clue>
                            <ktg-clue-title icon="warning">{{
                                $t('geschaeft_nicht_vollstaendig_err_txt')
                            }}</ktg-clue-title>
                        </ktg-clue>
                    </ktg-clues>
                    <div class="incomplete-display">
                        <div class="error-img">
                            <img :src="planungsgeschaeftAbschlussErrorImg" alt="Geschaeft nicht vollstaendig" />
                        </div>
                        <!--
                          TODO: It seems that the single check result display is not used anymore. We just
                          place a simple screenshot here, instead of the below's detailed error info,
                          and show the error info ONLY in dev mode:
                        -->
                        <div v-if="globalVar('environment') === 'dev'" class="mt-2_5rem">
                            <template v-for="(section, sectionKey, idx) in checkResult?.sections || {}">
                                <div v-if="!section.valid" :key="idx" class="my-0_5rem">
                                    <h6 class="ktg-title-06">
                                        {{ $t(String(sectionKey)) }}
                                        <RouterLink
                                            v-if="createRouteFromSection(sectionKey as string)"
                                            :to="createRouteFromSection(sectionKey as string)!"
                                            custom
                                            v-slot="{ navigate, href }"
                                        >
                                            <a :href="href" @click.prevent="navigate()">
                                                <ktg-icon name="link"></ktg-icon>
                                            </a>
                                        </RouterLink>
                                    </h6>
                                    <template v-for="(subSections, subDesc, subIdx) in section">
                                        <div v-if="!subSections.valid && subDesc !== 'valid'" :key="subIdx">
                                            <strong>{{ $t(subDesc as string) }}:</strong> {{ $t(subSections.message) }}
                                        </div>
                                    </template>
                                    <ktg-line class="mt-2rem mb-1rem"></ktg-line>
                                </div>
                            </template>
                        </div>
                        <!-- end dev output -->
                    </div>
                </div>
            </div>
            <!-- end !readonly -->

            <!--
        <v-card v-if="!readonly" class="mb-0_5rem" elevation="0">
            <v-form ref="commitform" v-model="formvalid" lazy-validation>
                <v-card-actions
                    v-if="
                        checkResult &&
                        checkResult.valid &&
                        (user.has_username_passkey || user.api_user || disableSignatures)
                    "
                >
                    <v-spacer />
                    <v-btn
                        color="action white--text"
                        :disabled="!checkResult || !checkResult.valid || !acceptFee || !acceptAgb"
                        :loading="verifyLoading || saving"
                        @click="commitPlanungsgeschaeft"
                    >
                        <v-icon left>fa-solid fa-paper-plane</v-icon>
                        {{ $t('commit_dossier') }}
                    </v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
        <v-card v-if="readonly" class="mb-0_5rem" elevation="0">
            <v-form ref="commitform" v-model="formvalid" lazy-validation>
                <v-card-title> {{ $t('confirmation') }}</v-card-title>
                <v-card-text>
                    <v-checkbox
                        v-model="alwaysOn"
                        :label="$t('fee_warning_bg')"
                        :rules="[(v) => !!v || $t('accept_fee')]"
                        :disabled="readonly"
                    ></v-checkbox>
                    <div class="d-flex align-center">
                        <v-checkbox
                            v-model="alwaysOn"
                            :disabled="readonly"
                            :rules="[(v) => !!v || $t('accept_agb_first')]"
                            required
                        >
                            <template #label>
                                <div v-html="$t('accept_agb') + '&nbsp'"></div>
                                <a target="_blank" href="https://www.tg.ch/rechtliches.html/546" @click.stop>
                                    {{ $t('geschaeftsbedingungen') }}
                                </a>
                            </template>
                        </v-checkbox>
                    </div>
                </v-card-text>
            </v-form>
        </v-card>
        <v-card
            v-if="record.aktueller_status_ebau_value === ECH_STATUS.WAITING_FOR_SIGNATURE && signUrl && qrSrc"
            class="mb-0_5rem"
            elevation="0"
        >
            <v-card-title> {{ $t('signature') }}</v-card-title>
            <v-card-text>
                {{ $t('signature_info') }}:<br />
                <a v-if="signUrl" :href="signUrl">{{ $t('fidentity_signature_link_txt') }}</a
                ><br />
                <v-img v-if="qrSrc" :src="qrSrc" max-height="200px" max-width="200px" />
            </v-card-text>
        </v-card>
            
            -->

            <ktg-line class="mt-2rem mb-1rem"></ktg-line>
            <PrevNextBtnBar
                :back-text="$t('back')"
                :next-text="$t('planungsgeschaeft_einreichen')"
                :disabled="saving"
                :loading="saving"
                :next-disabled="!ready4Submit"
                @next="onSubmitBtn"
                @back="onPrevBtn"
            />

            <!-- signature process in progress, show qr and urls -->
            <div
                v-if="
                    props.geschaeft.aktueller_status_ebau_value === ECH_STATUS.WAITING_FOR_SIGNATURE && signUrl && qrSrc
                "
                class="my-2_5rem"
            >
                <h6 class="ktg-title-06">{{ $t('signature') }}</h6>
                <p>
                    {{ $t('signature_info') }}:<br />
                    <a v-if="signUrl" :href="signUrl">{{ $t('fidentity_signature_link_txt') }}</a
                    ><br />
                    <img v-if="qrSrc" :src="qrSrc" style="max-width: 200px; max-height: 200px" />
                </p>
            </div>
        </fieldset>
        <UserLoginModalCircus
            :user="useAuthUser()"
            v-model="showLoginCircus"
            :redirectUrl="appUrl('/geschaefte/meine_geschaefte/' + props.geschaeft.id + '/abschluss')"
            :initialState="5"
        />
    </div>
</template>

<style lang="scss" scoped>
.incomplete-display {
    background-color: white;
    min-height: 50px;
    padding-top: 2.25rem;
    padding-bottom: 2.5rem;
}

.error-img {
  display: flex;
  justify-content: center;
}
</style>
